// AI-CONTEXT: See docs/features/participation-ladder-benchmark.md
import { client } from '../../hooks/Client'
import isSuperAdminTenant from '../../utils/superAdminTenant'
import PageHeader from '../../components/layout/PageHeader'
import BenchmarkHeader from '../../components/benchmarkDetails/BenchmarkHeader'
import BenchmarkPositionBlock from '../../components/benchmarkDetails/BenchmarkPositionBlock'
import BenchmarkAdminTable from '../../components/benchmarkDetails/BenchmarkAdminTable'
import BenchmarkActivitiesSection from '../../components/benchmarkDetails/BenchmarkActivitiesSection'
import BenchmarkMechanismsSection from '../../components/benchmarkDetails/BenchmarkMechanismsSection'
import useParticipationBenchmark from '../../hooks/useParticipationBenchmark'
import { isBenchmarkStrongEnough } from '../../helpers/dataHelpers/getBenchmarkStrength'

/**
 * Detail page for the cross-organisation participatieladder benchmark.
 * Growth is measured in treden (average step change begin → current per
 * organisation). Reuses the MSI benchmark detail blocks with unit='treden':
 * regular tenants get the anonymised scatter, the monitor tenant the named
 * table (viewMode is resolved server-side). Below that, for the superTenant
 * only: the werkzame-elementen and activiteiten sections — the raw analysis
 * material. The curated insights block used to sit here; curation moved to the
 * Intelligence page, and the tenant-facing view is a separate later feature.
 * Only the CBS-context section is absent (sector coupling not meaningful here).
 */
const ParticipationBenchmarkDetails = () => {
    const { benchmark, loading } = useParticipationBenchmark(true, { includeExplanation: true })

    // SuperTenant sees the raw analysis (werkzame elementen + activiteiten).
    // Normal tenants see the header + position block only.
    const isSuper = isSuperAdminTenant(client)

    // Normal tenants only see the benchmark from 'Sterke benchmark' strength up
    // (entry points are hidden too, but the URL stays reachable). The
    // superTenant keeps the full view to monitor maturing benchmarks.
    const strongEnough = isBenchmarkStrongEnough(benchmark)
    const hasBenchmark = !loading && benchmark && benchmark.benchmarkGrowth != null
    const showBenchmark = hasBenchmark && (isSuper || strongEnough)

    return (
        <div className='main'>
            <div className='main-container'>
                <div className='benchmark-details-container'>

                    <PageHeader
                        heading={'Benchmark participatieladder'}
                        description={'Hier zie je hoe de gemiddelde groei op de participatieladder van jullie medewerkers zich verhoudt tot andere organisaties. Groei is het verschil in treden tussen de beginsituatie en de huidige situatie, gemiddeld per organisatie.'}
                        AI={false}
                    />

                    {loading && <p>Gegevens worden geladen…</p>}

                    {!loading && (!benchmark || benchmark.benchmarkGrowth == null) && (
                        <p className='benchmark-details-empty'>Er is nog onvoldoende data voor een participatieladder-benchmark.</p>
                    )}

                    {hasBenchmark && !showBenchmark && (
                        <p className='benchmark-details-empty'>Deze benchmark is nog niet sterk genoeg om te tonen. Zodra genoeg organisaties met voldoende medewerkers meedoen, verschijnt hij hier.</p>
                    )}

                    {showBenchmark && (
                        <div className='benchmark-details-content'>
                            <BenchmarkHeader
                                result={benchmark}
                                unit='treden'
                                respondentLabel='Medewerkers'
                            />

                            {benchmark.viewMode === 'admin' ? (
                                <BenchmarkAdminTable
                                    positioning={benchmark.positioning}
                                    benchmarkGrowth={benchmark.benchmarkGrowth}
                                    unit='treden'
                                    respondentLabel='Medewerkers'
                                />
                            ) : (
                                <BenchmarkPositionBlock
                                    positioning={benchmark.positioning}
                                    benchmarkGrowth={benchmark.benchmarkGrowth}
                                    unit='treden'
                                />
                            )}

                            {/* Het inzichtenblok is hier weg — zie BenchmarksDetails.jsx
                                en docs/features/intelligence-research-questions.md (§2b, §5).
                                De ruwe analysesecties blijven: analysemateriaal, geen curatie. */}
                            {isSuper && (
                                <>
                                    <BenchmarkMechanismsSection
                                        explanation={benchmark.explanation}
                                    />

                                    <BenchmarkActivitiesSection
                                        activitiesOverview={benchmark.explanation?.activitiesOverview}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ParticipationBenchmarkDetails
